import { Popover, PopoverTrigger, PopoverContent, PopoverPortal } from "@radix-ui/react-popover";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { OnboardingStepIndicator } from "@/routes/ona-onboarding/OnboardingStepIndicator";
import { StepHeadline } from "@/routes/ona-onboarding/StepHeadline";
import { StepSubheader } from "@/routes/ona-onboarding/StepSubheader";
import IconWorld from "central-icons/IconWorld";
import { RepositorySelectorMenu } from "@/routes/ona-onboarding/RepositorySelectorMenu";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import {
    onboardingRunnerAtom,
    onboardingStateAtom,
    parsedRepositoryUrlAtom,
    currentStepAtom,
    ONBOARDING_STEP,
    ONBOARDING_STEP_SLUG,
    lastSelectedScmAuthHostAtom,
} from "@/routes/ona-onboarding/onboarding-state";
import { forwardRef, useCallback, useEffect } from "react";
import { useParseContextURL } from "@/queries/runner-queries";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { atomWithDefault } from "jotai/utils";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useSegmentTrackError, useSegmentTrack } from "@/hooks/use-segment";
import { useNavigate } from "react-router-dom";
import { PageCategories, usePageTracking } from "@/hooks/use-page-tracking";

const selectedRepositoryUrlAtom = atom(
    (get) => get(onboardingStateAtom).selectedRepositoryUrl,
    (get, set, selectedRepositoryUrl: string | null) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), selectedRepositoryUrl });
    },
);

const canContinueAtom = atom((get) => {
    const parsedRepositoryUrl = get(parsedRepositoryUrlAtom);
    return Boolean(parsedRepositoryUrl?.git);
});

const isRepositorySelectorOpenAtom = atomWithDefault((get) => !get(selectedRepositoryUrlAtom));

export const SelectRepository = () => {
    usePageTracking("Select Repository", PageCategories.OnaOnboarding);

    const store = useStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (!store.get(lastSelectedScmAuthHostAtom)) {
            navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.connectToScm}`, {
                replace: true,
            });
        }
    }, [store, navigate]);

    return (
        <div className="mx-0 mt-0 flex flex-col items-center justify-start p-8 pt-16 md:m-auto md:justify-center md:pt-8">
            <OnboardingStepIndicator className="mb-8" />
            <StepHeadline className="mb-2 mt-3">Choose a repository for Ona</StepHeadline>
            <StepSubheader className="mb-10">
                Ona will only access the repository you select. You can change this later.
            </StepSubheader>
            <RepositorySelectorPopover />
            <ContinueButton className="mt-8" />
        </div>
    );
};

const RepositorySelectorPopover = () => {
    const [isRepositorySelectorOpen, setIsRepositorySelectorOpen] = useAtom(isRepositorySelectorOpenAtom);
    const setSelectedRepositoryUrl = useSetAtom(selectedRepositoryUrlAtom);
    const setParsedRepositoryUrl = useSetAtom(parsedRepositoryUrlAtom);
    const store = useStore();
    const segmentTrack = useSegmentTrack();

    const selectRepository = useCallback(
        (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => {
            const selectedRepositoryUrl = store.get(selectedRepositoryUrlAtom);
            if (selectedRepositoryUrl !== repoUrl) {
                setSelectedRepositoryUrl(repoUrl);
                setParsedRepositoryUrl(null);

                // Track repository selection
                segmentTrack("onboarding_repository_selected", {
                    location: "onboarding_select_repository",
                    repository_name: repoName,
                    repository_url: repoUrl,
                    selection_index: selectionIndex,
                    total_available: totalAvailable,
                });
            }
            setIsRepositorySelectorOpen(false);
        },
        [setSelectedRepositoryUrl, setParsedRepositoryUrl, setIsRepositorySelectorOpen, store, segmentTrack],
    );

    return (
        <Popover open={isRepositorySelectorOpen} onOpenChange={setIsRepositorySelectorOpen}>
            <PopoverTrigger asChild>
                <SelectRepositoryButton className="mb-3" />
            </PopoverTrigger>
            <PopoverPortal>
                <PopoverContent className="w-screen p-2 md:w-128">
                    <RepositorySelectorMenu onSelect={selectRepository} />
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};

const SelectRepositoryButton = forwardRef<HTMLButtonElement, { className?: string }>(function SelectRepositoryButton(
    { className, ...props },
    ref,
) {
    const [selectedRepositoryUrl, setSelectedRepositoryUrl] = useAtom(selectedRepositoryUrlAtom);
    const onboardingRunnerQuery = useAtomValue(onboardingRunnerAtom);
    const setParsedRepositoryUrl = useSetAtom(parsedRepositoryUrlAtom);
    const setIsRepositorySelectorOpen = useSetAtom(isRepositorySelectorOpenAtom);
    const { toast } = useToast();
    const trackError = useSegmentTrackError();

    const {
        data: parsedRepositoryUrl,
        isLoading: isParsing,
        error: parseError,
    } = useParseContextURL(
        onboardingRunnerQuery.type === "success" ? onboardingRunnerQuery.data.runnerId : undefined,
        selectedRepositoryUrl ?? undefined,
        { enabled: Boolean(selectedRepositoryUrl) },
    );

    let buttonLabel = "";
    if (!selectedRepositoryUrl) {
        buttonLabel = "Select repository";
    } else if (isParsing) {
        buttonLabel = "Resolving repository…";
    } else if (parsedRepositoryUrl?.git?.repo) {
        buttonLabel = parsedRepositoryUrl.git.repo;
    } else {
        buttonLabel = "Select repository";
    }

    useEffect(() => {
        if (parsedRepositoryUrl) {
            setParsedRepositoryUrl(parsedRepositoryUrl);
        }
    }, [parsedRepositoryUrl, setParsedRepositoryUrl]);

    useEffect(() => {
        if (parseError) {
            setSelectedRepositoryUrl(null);
            setParsedRepositoryUrl(null);
            setIsRepositorySelectorOpen(true);
            toast({
                title: "Failed to resolve selected repository",
                description: formatError(parseError),
            });
            trackError("onboarding_parse_repo_url_error", parseError);
        }
    }, [parseError, setSelectedRepositoryUrl, setParsedRepositoryUrl, setIsRepositorySelectorOpen, trackError, toast]);

    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                "flex h-10 items-center truncate rounded-lg border border-brand-foreground px-8 py-2 font-medium ring-4 ring-brand-foreground/10 hover:bg-surface-base disabled:cursor-not-allowed disabled:opacity-50 dark:ring-brand-foreground/50 dark:hover:bg-[rgb(var(--ona-gray-900))]",
                className,
            )}
            disabled={!!selectedRepositoryUrl && isParsing}
            data-tracking-id="select-repository-onboarding"
            {...props}
        >
            <span className="mr-2">
                {selectedRepositoryUrl && isParsing ? (
                    <IconSpinner className="size-4 animate-spin" size="lg" />
                ) : (
                    <IconWorld className="size-4" />
                )}
            </span>
            <span>{buttonLabel}</span>
        </button>
    );
});

const ContinueButton = ({ className }: { className?: string }) => {
    const canContinue = useAtomValue(canContinueAtom);
    const navigate = useNavigate();
    const setCurrentStep = useSetAtom(currentStepAtom);

    const onClick = () => {
        if (canContinue) {
            setCurrentStep(ONBOARDING_STEP[ONBOARDING_STEP_SLUG.getStartedWithTasks]);
            navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.getStartedWithTasks}`);
        }
    };

    return (
        <Button
            variant="primary"
            className={cn("h-10 px-8 py-2", { invisible: !canContinue }, className)}
            size="lg"
            disabled={!canContinue}
            onClick={onClick}
            data-tracking-id="continue-onboarding-select-repository-page"
        >
            Continue
        </Button>
    );
};
